/**
 * Trilingual UI strings for the chat inbox in the legacy `frontend/` app.
 *
 * Mirrors the module shape used by `shareUi.ts` and `quizUi.ts`
 * (`getChatInboxUiStrings(resolveChatInboxLocale())`) so consumers can adopt
 * the same `const strings = getChatInboxUiStrings(resolveChatInboxLocale())`
 * pattern without learning a new resolver.
 */
export type ChatInboxLocale = "en-US" | "ja-JP" | "ko-KR";

/**
 * Shape of the localized strings used by the chat inbox. Keys are stable
 * contract values; do not rename without updating all locale blocks below.
 */
export interface ChatInboxStrings {
    searchPlaceholder: string;
    composeAriaLabel: string;
    messagesHeading: string;
    requestsLink: string;
    requestsHeading: string;
    backAriaLabel: string;
    /**
     * ICU-style template with a `{count}` argument describing how many
     * unread messages a conversation has. The placeholder is substituted
     * with a raw number by the caller using `formatChatInboxTemplate`.
     */
    unreadDotAriaLabel: string;
    /**
     * Preview shown when a conversation has unread messages. Accepts a
     * `{count}` argument resolved via `formatChatInboxTemplate`.
     */
    newMessagesPreview: string;
    noMessagesYet: string;
    emptyInboxTitle: string;
    emptyInboxSubtitle: string;
    emptyRequestsTitle: string;
    emptyRequestsSubtitle: string;
    chooseConversationTitle: string;
    chooseConversationSubtitle: string;
    /**
     * Realtime in-app toast title for a direct chat message. Uses
     * `{name}` for the sender display name; resolved with
     * `formatChatInboxTemplate`.
     */
    toastTitleDirect: string;
    /**
     * Realtime toast title for group/team chat messages. Uses `{title}`
     * for the conversation title and `{name}` for the sender.
     */
    toastTitleGroup: string;
    /** Realtime toast title for incoming chat requests. Uses `{name}`. */
    toastTitleRequest: string;
    /** Fallback preview when a message body is unavailable (e.g. media-only). */
    toastNoPreview: string;
    /**
     * Aria-label template for the entire toast container. Uses `{name}`
     * (sender) so screen-reader users hear the full intent.
     */
    toastAriaLabelOpenChat: string;
    /** Aria-label for the close button inside the chat toast. */
    toastAriaLabelDismiss: string;
}

const STRINGS: Record<ChatInboxLocale, ChatInboxStrings> = {
    "en-US": {
        searchPlaceholder: "Search",
        composeAriaLabel: "New message",
        messagesHeading: "Messages",
        requestsLink: "Requests",
        requestsHeading: "Chat requests",
        backAriaLabel: "Back to messages",
        unreadDotAriaLabel: "{count} unread messages",
        newMessagesPreview: "{count} new messages",
        noMessagesYet: "No messages yet",
        emptyInboxTitle: "Your messages",
        emptyInboxSubtitle: "Send private messages to a friend or a partea.",
        emptyRequestsTitle: "No chat requests",
        emptyRequestsSubtitle:
            "Requests from people outside your default messaging rules will appear here.",
        chooseConversationTitle: "Choose a conversation",
        chooseConversationSubtitle: "Select a chat from the left to read or reply.",
        toastTitleDirect: "{name}",
        toastTitleGroup: "{title} \u00b7 {name}",
        toastTitleRequest: "{name} sent a chat request",
        toastNoPreview: "Sent you a message",
        toastAriaLabelOpenChat: "Open conversation with {name}",
        toastAriaLabelDismiss: "Dismiss chat preview",
    },
    "ja-JP": {
        searchPlaceholder: "検索",
        composeAriaLabel: "新しいメッセージ",
        messagesHeading: "メッセージ",
        requestsLink: "リクエスト",
        requestsHeading: "チャットリクエスト",
        backAriaLabel: "メッセージに戻る",
        unreadDotAriaLabel: "未読メッセージ {count} 件",
        newMessagesPreview: "新着メッセージ {count} 件",
        noMessagesYet: "メッセージはまだありません",
        emptyInboxTitle: "メッセージ",
        emptyInboxSubtitle: "友だちやパルティーにダイレクトメッセージを送りましょう。",
        emptyRequestsTitle: "チャットリクエストはありません",
        emptyRequestsSubtitle:
            "デフォルトの受信設定の対象外からのリクエストは、ここに表示されます。",
        chooseConversationTitle: "会話を選んでください",
        chooseConversationSubtitle: "左側のチャットを選んで読んだり返信したりできます。",
        toastTitleDirect: "{name}",
        toastTitleGroup: "{title} \u30fb {name}",
        toastTitleRequest: "{name} さんからチャットリクエストが届きました",
        toastNoPreview: "メッセージが届きました",
        toastAriaLabelOpenChat: "{name} さんとの会話を開く",
        toastAriaLabelDismiss: "チャットプレビューを閉じる",
    },
    "ko-KR": {
        searchPlaceholder: "검색",
        composeAriaLabel: "새 메시지",
        messagesHeading: "메시지",
        requestsLink: "요청",
        requestsHeading: "채팅 요청",
        backAriaLabel: "메시지로 돌아가기",
        unreadDotAriaLabel: "읽지 않은 메시지 {count}개",
        newMessagesPreview: "새 메시지 {count}개",
        noMessagesYet: "아직 메시지가 없습니다",
        emptyInboxTitle: "메시지",
        emptyInboxSubtitle: "친구나 파르티아에게 개인 메시지를 보내세요.",
        emptyRequestsTitle: "채팅 요청이 없습니다",
        emptyRequestsSubtitle:
            "기본 메시지 수신 규칙 밖에서 온 요청은 여기에 표시됩니다.",
        chooseConversationTitle: "대화를 선택하세요",
        chooseConversationSubtitle: "왼쪽에서 채팅을 선택해 읽거나 답장하세요.",
        toastTitleDirect: "{name}",
        toastTitleGroup: "{title} \u00b7 {name}",
        toastTitleRequest: "{name}님이 채팅 요청을 보냈습니다",
        toastNoPreview: "메시지를 보냈습니다",
        toastAriaLabelOpenChat: "{name}님과의 대화 열기",
        toastAriaLabelDismiss: "채팅 미리보기 닫기",
    },
};

/**
 * Resolve a supported browser locale for the chat inbox UI. Falls back to
 * `en-US` in non-browser environments (SSR, tests) or when the browser
 * language is not one of the supported locales.
 */
export function resolveChatInboxLocale(): ChatInboxLocale {
    if (typeof navigator === "undefined" || !navigator.language) {
        return "en-US";
    }

    const language = navigator.language.toLowerCase();
    if (language.startsWith("ja")) {
        return "ja-JP";
    }
    if (language.startsWith("ko")) {
        return "ko-KR";
    }

    return "en-US";
}

/**
 * Return the chat inbox strings for the requested or detected locale. When
 * an unsupported locale slips in the `en-US` strings are returned so the
 * UI always renders.
 */
export function getChatInboxUiStrings(locale?: ChatInboxLocale): ChatInboxStrings {
    const resolvedLocale = locale ?? resolveChatInboxLocale();
    return STRINGS[resolvedLocale] ?? STRINGS["en-US"];
}

/**
 * Resolve a single `{name}` placeholder inside a localized template. This
 * is intentionally minimal so we do not pull in a full ICU runtime for
 * one or two substitutions; it mirrors the helper used in
 * `ConversationPicker.tsx` and accepts numbers or strings as values.
 */
export function formatChatInboxTemplate(
    template: string,
    values: Record<string, number | string>
): string {
    return Object.entries(values).reduce(
        (result, [key, value]) => result.replace(`{${key}}`, String(value)),
        template
    );
}
